import type { Sim, SimEvent } from '../../game/simulation';
import type { Panel } from './Panel';

/**
 * Owns every overlay panel and enforces the "one open at a time" rule.
 *
 * Panels register themselves with an id. UI code calls open(id), toggle(id),
 * or close(). The manager subscribes to the sim bus and auto-opens the
 * panel mapped to inspect events (mailbox / truck / mill / storage).
 */
export class PanelManager {
  private panels = new Map<string, Panel>();
  private active: Panel | null = null;
  private host: HTMLElement;
  /** Per-panel onOpen callback — used to seed transient state (mill coords). */
  private openHooks = new Map<string, (ev: Extract<SimEvent, { type: 'inspect' }>) => void>();

  constructor(host: HTMLElement, private sim: Sim) {
    this.host = host;
    sim.bus.on((e) => {
      if (e.type === 'inspect') {
        const mapping: Record<string, string> = {
          storage: 'storage',
          mill: 'mill',
          mailbox: 'mailbox',
          truck: 'truck',
          toolrack: 'toolrack',
          seeds: 'seeds',
        };
        const id = mapping[e.kind];
        if (id) {
          this.openHooks.get(id)?.(e);
          this.open(id);
        }
      }
    });
  }

  register(panel: Panel, onOpen?: (e: Extract<SimEvent, { type: 'inspect' }>) => void) {
    this.panels.set(panel.id(), panel);
    panel.mount(this.host);
    if (onOpen) this.openHooks.set(panel.id(), onOpen);
  }

  open(id: string) {
    const next = this.panels.get(id);
    if (!next) return;
    if (this.active && this.active !== next) this.active.hide();
    this.active = next;
    next.show();
  }

  toggle(id: string) {
    const p = this.panels.get(id);
    if (!p) return;
    if (p.isVisible()) {
      p.hide();
      this.active = null;
    } else {
      this.open(id);
    }
  }

  close() {
    if (this.active) {
      this.active.hide();
      this.active = null;
    }
  }

  activeId(): string | null {
    if (!this.active) return null;
    for (const [id, p] of this.panels) if (p === this.active) return id;
    return null;
  }

  tick() {
    if (this.active) this.active.tick();
  }
}
